import { CheckCircleIcon, EnvelopeIcon, SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { subscribeToNewsletter } from '../services/newsletterService'

type Props = {
  email: string | null
  isOpen: boolean
  onClose: () => void
}

export const NewsletterOptInModal = ({ email, isOpen, onClose }: Props) => {
  const [submitting, setSubmitting] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen || !email) {
    return null
  }

  const handleSubscribe = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const message = await subscribeToNewsletter(email)
      setSuccessMessage(message)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not subscribe right now.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    setSuccessMessage(null)
    setError(null)
    setSubmitting(false)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-app-ink/35 dark:bg-black/60 px-4 backdrop-blur-sm">
      <div className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-app-line bg-app-surface shadow-2xl">
        <button
          type="button"
          onClick={handleClose}
          disabled={submitting}
          className="absolute right-4 top-4 rounded-full p-1.5 text-app-muted transition hover:bg-app-subtle hover:text-app-ink disabled:opacity-50"
          aria-label="Close newsletter prompt"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="border-b border-app-line bg-app-subtle px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-100 text-sky-700 dark:bg-app-highlight dark:text-app-accent">
              {successMessage ? <CheckCircleIcon className="h-6 w-6" /> : <EnvelopeIcon className="h-6 w-6" />}
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700 dark:text-app-accent">
                Welcome to JobTrackr.it
              </p>
              <h2 className="mt-1 text-xl font-bold text-app-ink font-display">
                {successMessage ? 'You are subscribed' : 'Want job search notes in your inbox?'}
              </h2>
            </div>
          </div>
        </div>

        <div className="px-6 py-6">
          {successMessage ? (
            <div className="space-y-5">
              <p className="text-sm leading-6 text-app-muted">
                {successMessage} We will send practical product updates and job search ideas to{' '}
                <span className="font-semibold text-app-ink">{email}</span>.
              </p>
              <button
                type="button"
                onClick={handleClose}
                className="w-full bg-app-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-app-primary-hover rounded-lg"
              >
                Continue to dashboard
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              <p className="text-sm leading-6 text-app-muted">
                Get occasional product updates, resume tips, and lightweight systems for keeping your job search moving.
                We will use your account email:
              </p>

              <div className="flex items-center gap-3 rounded-xl border border-app-line bg-app-subtle px-4 py-3">
                <EnvelopeIcon className="h-5 w-5 text-app-muted" />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-app-ink">
                  {email}
                </span>
              </div>

              <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 dark:border-app-line dark:bg-app-highlight">
                <div className="flex gap-3">
                  <SparklesIcon className="mt-0.5 h-5 w-5 flex-none text-sky-700 dark:text-app-accent" />
                  <p className="text-sm leading-6 text-app-ink">
                    No daily blasts. Just useful notes, and you can unsubscribe any time.
                  </p>
                </div>
              </div>

              {error && (
                <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300">
                  {error}
                </p>
              )}

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={submitting}
                  className="border border-app-line px-4 py-3 text-sm font-semibold text-app-ink transition hover:bg-app-subtle disabled:opacity-50 rounded-lg"
                >
                  Not now
                </button>
                <button
                  type="button"
                  onClick={handleSubscribe}
                  disabled={submitting}
                  className="bg-app-primary px-5 py-3 text-sm font-semibold text-white transition hover:bg-app-primary-hover disabled:cursor-not-allowed disabled:opacity-60 rounded-lg"
                >
                  {submitting ? 'Subscribing...' : 'Subscribe me'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
